import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import TrueImpactText from '../../../design-system/base/TrueImpactText';
import { COLORS } from '../../../design-system/settings/variables/Colors';
import { formattedDate } from '../../../utils/date';
import {
  faFloppyDisk,
  faPencil,
  faTrash,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

export type ModeState = 'show' | 'edit' | 'add';

interface EditAndSaveRowProps {
  title: string;
  description: string | undefined;
  createdDate: string | undefined;
  userName: string;
  onClick: () => unknown;
  onDelete: () => unknown;
  onSave: (title: string, description: string | undefined) => unknown;
}

const isValid = (title: string, description: string | undefined) => {
  if (description === undefined) return title.length > 3;
  return title.length >= 3 && description.length >= 3;
};

const StyledDiv = styled.div<{ state: ModeState }>`
  padding: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: background 0.3s ease-in-out;
  .filter-name-container {
    flex: 1 1 auto;
    cursor: ${({ state }: { state: ModeState }) =>
      state === 'show' ? 'pointer' : 'default'};
    .edit-name-field,
    .edit-description-field {
      width: 100%;
      padding: 8px 16px;
      border-radius: 4px;
      outline: none;
      border: none;
      margin-bottom: 8px;
      background-color: ${COLORS.greyBase};
    }
    .edit-description-field {
      resize: vertical;
      min-height: 120px;
    }
  }
  &:hover {
    background-color: ${({ state }: { state: ModeState }) =>
      state === 'show' ? COLORS.ctaPrimaryBackground : 'inherit'};
  }
  .filter-name,
  .filter-description {
    margin-bottom: 8px;
  }
  .icons-wrapper {
    display: flex;
    margin-left: 16px;
    svg {
      cursor: pointer;
    }
    .pencil-icon,
    .save-icon {
      margin-right: 16px;
    }
    .save-icon path {
      transition: all 0.5s ease-in-out;
      fill: ${COLORS.ctaPrimary};
    }
    .save-icon.disabled path {
      fill: ${COLORS.greyBase};
    }
  }
`;

const EditAndSaveRow: React.FC<EditAndSaveRowProps> = ({
  title,
  description: des,
  createdDate,
  userName,
  onDelete,
  onSave,
  onClick,
}) => {
  const [name, setName] = useState<string>(title);
  const [state, setState] = useState<ModeState>('show');
  const [description, setDescription] = useState<string | undefined>(des);

  const handleSave = () => {
    onSave(name, description);
    setState('show');
  };
  const handleKeyPress = (
    e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    if (e.key === 'Enter') handleSave();
  };

  const handleName = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setName(value);
    },
    [setName],
  );

  const handleDescription = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const value = e.target.value;
      setDescription(value);
    },
    [setDescription],
  );

  return (
    <StyledDiv state={state}>
      <div
        className="filter-name-container"
        onClick={state === 'show' ? onClick : undefined}
      >
        {state === 'show' ? (
          <TrueImpactText variant="body-bold" className="filter-name">
            {name}
          </TrueImpactText>
        ) : (
          <input
            value={name}
            onChange={handleName}
            onKeyPress={handleKeyPress}
            className="edit-name-field"
            autoFocus={true}
          />
        )}
        {description === undefined ? null : state === 'show' ? (
          <TrueImpactText className="filter-description">
            {description}
          </TrueImpactText>
        ) : (
          <textarea
            value={description}
            onChange={handleDescription}
            onKeyPress={handleKeyPress}
            className="edit-description-field"
          />
        )}
        <TrueImpactText variant="body-small" color="greyBase">
          Created: {formattedDate(createdDate)} by {userName}
        </TrueImpactText>
      </div>
      <div className="icons-wrapper">
        {state === 'show' ? (
          <FontAwesomeIcon
            icon={faPencil}
            onClick={() => setState('edit')}
            className="pencil-icon"
          />
        ) : (
          <FontAwesomeIcon
            icon={faFloppyDisk}
            onClick={handleSave}
            className={`save-icon${
              isValid(title, description) ? '' : ' disabled'
            }`}
          />
        )}
        <FontAwesomeIcon icon={faTrash} onClick={onDelete} />
      </div>
    </StyledDiv>
  );
};

export default EditAndSaveRow;
